import { useState, useMemo } from 'react';
import {
    Button,
    Input,
    Loading,
    RadioGroup,
    Radio,
    Switch,
} from '@appkit4/react-components';
import ListingTable from '../../../shared/listingTable/ListingTable';
import ExportModal from '../exportModal/ExportModal';
import UploadModal from '../../../shared/uploadModal/UploadModal';
import PDFViewerModal from '../../../shared/pdfViewerModal/PDFViewerModal';
import { useNavigate, useParams } from 'react-router-dom';
import './OnlineFormFillerTable.scss';
import { formatDate } from '../../../../helpers/helperFunctions';
import {
    usePostV1DocumentsDeleteDocument,
    usePostV1DocumentsDownloadDocuments,
    usePutV1DocumentsUpdateReadyForSubmission,
    useGetV1DocumentTypeByProcessTypeProcessTypeId,
} from '../../../../api/generated/endpoint';
import { DownloadDocumentsRequest } from '../../../../api/generated/models';
import { DeleteModal } from '../../../shared/deleteModal';
import Illustration6 from '../../../../assets/illustrations/Illustration_6.png';
import useToast from '../../../../helpers/useToast';
import useLocale from '../../../../locale/useLocale';
import DocumentTypeFilter from '../../../shared/documentTypeFilter/DocumentTypeFilter';

interface OnlineFormFillerTableProps {
    filteredData: any[];
    currentPage: number;
    pageSize: number;
    setCurrentPage: (page: number) => void;
    totalCount: number;
    isLoading?: boolean;
    activeTab?: number;
    filterDocTypes?: string[];
    onTabChange?: (index: number) => void;
    processName?: string;
    onDocumentUpdate?: () => void;
    onUploadComplete?: () => void;
    processTypeId?: number;
    processDocumentTypeNames?: string[];
}

const OnlineFormFillerTable = ({
    filteredData,
    currentPage,
    pageSize,
    setCurrentPage,
    isLoading = false,
    activeTab = 0,
    filterDocTypes,
    onTabChange,
    processName,
    onDocumentUpdate,
    onUploadComplete,
    processTypeId,
    processDocumentTypeNames: processDocTypeNamesProp,
}: OnlineFormFillerTableProps) => {
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [isExportModalVisible, setIsExportModalVisible] = useState(false);
    const [confirmationVisible, setConfirmationVisible] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [selectedDocumentId, setSelectedDocumentId] = useState<number>();
    const [selectedDocTypes, setSelectedDocTypes] = useState<string[]>(
        filterDocTypes || []
    );
    const [isPdfModalVisible, setIsPdfModalVisible] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<number | null>(
        null
    );
    const [reportMisclassification, setReportMisclassification] =
        useState<boolean>(false);

    const { mutate: documentDownloadRequests, isLoading: isExporting } =
        usePostV1DocumentsDownloadDocuments();
    const { mutate: deleteDocument } = usePostV1DocumentsDeleteDocument();
    const { mutate: updateReadyForSubmission } =
        usePutV1DocumentsUpdateReadyForSubmission();
    const showToast = useToast();
    const { l } = useLocale();
    const { processId } = useParams<{ processId: string }>();
    const navigate = useNavigate();
    const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);

    const { data: allDocTypes } =
        useGetV1DocumentTypeByProcessTypeProcessTypeId(processTypeId || 3);

    const areAllSelectedDocumentsReadyForExport = useMemo(() => {
        // Determine the completion status based on process type
        const completionStatus =
            processTypeId === 2
                ? 18 // Fill PDF: Fill PDF Generated Successfully
                : 16; // Fill Online Form: Online Form Generated Successfully

        // Check if there are any completed documents in the dataset
        const hasCompletedDocuments = filteredData.some(
            (doc) => doc.processStatus === completionStatus
        );
        if (!hasCompletedDocuments) return false;

        // If no documents are selected, allow export (will export all completed)
        if (selectedIds.length === 0) return true;

        // Check if all selected documents have completed status
        const selectedDocuments = filteredData.filter((doc) =>
            selectedIds.includes(doc.documentId)
        );
        return selectedDocuments.every(
            (doc) => doc.processStatus === completionStatus
        );
    }, [selectedIds, filteredData, processTypeId]);

    // Status mappings based on process type
    const FillPdfValidationMapping: Record<string, number | number[]> = {
        'Awaiting Extraction': [2],
        'Awaiting Validation': [4, 9],
        'Validation Failed': [6],
        'Validation Low Confidence': [7],
        'Validation Passed': [8],
        'System Error': [1, 3, 5],
        'Awaiting Fill PDF Generation': [17],
        'Fill PDF Generated Successfully': [18],
    };

    const FillOnlineFormValidationMapping: Record<string, number | number[]> = {
        'Awaiting Extraction': [2],
        'Awaiting Validation': [4, 9],
        'Validation Failed': [6],
        'Validation Low Confidence': [7],
        'Validation Passed': [8],
        'User Approved': [10],
        'PDF Generation Failed': [11],
        'PDF Generated': [12],
        'System Error': [1, 3, 5],
        'Awaiting Online Form Generation': [13],
        'Online Form Generation Started': [14],
        'Online Form Generation System Error': [15],
        'Online Form Generated Successfully': [16],
    };

    // Use the appropriate mapping based on process type
    const ValidationMapping =
        processTypeId === 2
            ? FillPdfValidationMapping
            : FillOnlineFormValidationMapping;

    const getStatusByNumber = (num: number) => {
        for (const [key, value] of Object.entries(ValidationMapping)) {
            if (Array.isArray(value) ? value.includes(num) : value === num) {
                return key;
            }
        }
        return undefined;
    };

    const filterActionData = (data: any[]) => {
        return data.filter((row) => {
            const statusNum = row.processStatus;
            // Actions = errors and validation issues that require user intervention
            // System errors: 1 (SplitDocumentSystemError), 3 (ExtractionSystemError), 5 (ValidationSystemError), 11 (PdfGenerationSystemError), 15 (OnlineFormGenerationSystemError)
            // Validation issues: 6 (ValidationFailed), 7 (ValidationLowConfidence)
            return [1, 3, 5, 6, 7, 11, 15].includes(statusNum);
        });
    };

    const filterCompleteData = (data: any[]) => {
        return data.filter((row) => {
            const statusNum = row.processStatus;
            if (processTypeId === 2) {
                // Fill PDF: Complete when status is 18 (FillPDFGeneratedSuccessfully)
                return statusNum === 18;
            } else {
                // Fill Online Form (processTypeId === 3 or default): Complete when status is 16 (OnlineFormGeneratedSuccessfully)
                return statusNum === 16;
            }
        });
    };

    const filterReadyForSubmissionData = (data: any[]) => {
        return data.filter((row) => {
            // Show all documents marked as ready for submission
            return row.readyForSubmission === true;
        });
    };

    const processDocumentTypeNames = useMemo(() => {
        if (processDocTypeNamesProp && processDocTypeNamesProp.length > 0) {
            return processDocTypeNamesProp;
        }
        if (filterDocTypes && filterDocTypes.length > 0) {
            return filterDocTypes;
        }
        return Array.from(
            new Set(filteredData.map((item) => item.documentType))
        );
    }, [processDocTypeNamesProp, filterDocTypes, filteredData]);

    const getUniqueDocTypes = useMemo(() => {
        return processDocumentTypeNames.map((type) => ({
            value: type,
            desc: type,
        }));
    }, [processDocumentTypeNames]);

    const processDocumentTypeIds = useMemo(() => {
        if (!allDocTypes || allDocTypes.length === 0) {
            return [];
        }
        const nameSet = new Set(processDocumentTypeNames);
        return allDocTypes
            .filter(
                (dt) =>
                    dt.documentTypeName &&
                    nameSet.has(dt.documentTypeName) &&
                    dt.documentTypeId !== undefined &&
                    dt.documentTypeId !== null
            )
            .map((dt) => dt.documentTypeId as number);
    }, [allDocTypes, processDocumentTypeNames]);

    const filterByDocType = (data: any[]) => {
        if (selectedDocTypes.length === 0) return data;
        return data.filter((item) =>
            selectedDocTypes.includes(item.documentType)
        );
    };

    const handleEdit = (row: any) => {
        const route =
            processTypeId === 2
                ? `/process/${processId}/fill-pdf/${row.documentId}`
                : `/process/${processId}/online-form/${row.documentId}`;

        const stateKey =
            processTypeId === 2 ? 'fillPdfListingTab' : 'onlineFormListingTab';

        navigate(route, {
            state: {
                documentId: row.documentId,
                parentLocation: `/process/${processId}/uploaded`,
                fileName: row.parentDocument,
                parentName: processName,
                [stateKey]: activeTab,
                selectedDocTypes: selectedDocTypes,
            },
        });
    };

    const handleDelete = (row: any) => {
        setSelectedDocumentId(row.documentId);
        setConfirmationVisible(true);
    };

    const confirmDeletion = () => {
        deleteDocument(
            {
                data: {
                    documentIds: [Number(selectedDocumentId)],
                    reportMisclassification: reportMisclassification,
                },
            },
            {
                onSuccess: () => {
                    showToast(
                        l('_success'),
                        'Document has been deleted',
                        'success'
                    );
                    setConfirmationVisible(false);
                    setReportMisclassification(false);
                    setTimeout(() => {
                        if (onDocumentUpdate) {
                            onDocumentUpdate();
                        }
                    }, 3000);
                },
                onError: () => {
                    showToast(
                        l('_error'),
                        'Failed to delete document',
                        'error'
                    );
                },
            }
        );
    };

    const handleUpload = () => {
        setIsUploadModalVisible(true);
    };

    const handleUploadComplete = () => {
        if (onUploadComplete) {
            onUploadComplete();
        }
    };

    const handleExpand = (row: any) => {
        setSelectedDocument(row.documentId);
        setIsPdfModalVisible(true);
    };

    const handleReadyForSubmissionToggle = (
        documentId: number,
        checked: boolean
    ) => {
        updateReadyForSubmission(
            {
                data: {
                    documentId: documentId,
                    readyForSubmission: checked,
                },
            },
            {
                onSuccess: () => {
                    showToast(
                        l('_success'),
                        'Document ready for submission status updated',
                        'success'
                    );
                    if (onDocumentUpdate) {
                        onDocumentUpdate();
                    }
                },
                onError: () => {
                    showToast(
                        l('_error'),
                        'Failed to update document status',
                        'error'
                    );
                },
            }
        );
    };

    const handleExport = () => {
        // If documents are selected and ProcessType is 2 or 3, export directly without modal
        if (
            (processTypeId === 2 || processTypeId === 3) &&
            selectedIds.length > 0
        ) {
            const docDownloadRequest: DownloadDocumentsRequest = {
                documentIds: selectedIds,
                downloadAsZip: true,
                processType: processTypeId,
                processId: processId ? parseInt(processId) : undefined,
            };

            documentDownloadRequests(
                { data: docDownloadRequest },
                {
                    onSuccess: async (response: any) => {
                        const link: any = document.createElement('a');
                        link.href = response.sasUrl;

                        const url = new URL(response.sasUrl);
                        const pathname = url.pathname;
                        const filename = pathname.substring(
                            pathname.lastIndexOf('/') + 1
                        );
                        link.download = filename || 'download';

                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    },
                    onError: (error) => {
                        console.log(error);
                        showToast(
                            l('_error'),
                            'Failed to download documents',
                            'error'
                        );
                    },
                }
            );
        } else {
            // Show export modal for data export
            setIsExportModalVisible(true);
        }
    };

    const tabs = [
        { label: 'All', value: 'all' },
        { label: 'Actions', value: 'actions' },
        { label: 'Complete', value: 'complete' },
        ...(processTypeId === 3
            ? [{ label: 'Ready for Submission', value: 'ready' }]
            : []),
    ];

    const columns = [
        {
            field: 'documentType',
            header: (
                <div className="document-type-header">
                    <span>Document Type</span>
                    <DocumentTypeFilter
                        onSelect={(value: any) => setSelectedDocTypes(value)}
                        options={getUniqueDocTypes}
                        selectedDocTypes={selectedDocTypes}
                    />
                </div>
            ),
            sortable: true,
        },
        {
            field: 'uploadedDate',
            header: 'Upload Date',
            renderCell: (row: any) => (
                <span>{formatDate(row.uploadedDate)}</span>
            ),
        },
        {
            field: 'processStatus',
            header: 'Process Status',
            renderCell: (row: any) => {
                const getStatusIcon = (status: any) => {
                    switch (status) {
                        case 'Validation Failed':
                            return (
                                <span className="Appkit4-icon icon-warning-fill status-icon error-icon" />
                            );
                        case 'System Error':
                            return (
                                <span className="Appkit4-icon icon-warning-fill status-icon error-icon" />
                            );
                        case 'Awaiting Validation':
                            return (
                                <span className="status-dot pending-icon">
                                    ●
                                </span>
                            );
                        case 'Awaiting Extraction':
                            return (
                                <span className="status-dot pending-icon">
                                    ●
                                </span>
                            );
                        case 'Validation Passed':
                            return (
                                <span className="status-dot success-icon">
                                    ●
                                </span>
                            );
                        case 'Validation Low Confidence':
                            return (
                                <span className="Appkit4-icon icon-warning-fill custom-warning-icon status-icon warning-icon" />
                            );
                        case 'PDF Generated':
                            return (
                                <span className="status-dot success-icon">
                                    ●
                                </span>
                            );
                        case 'User Approved':
                            return (
                                <span className="status-dot success-icon">
                                    ●
                                </span>
                            );
                        case 'PDF Generation Failed':
                            return (
                                <span className="Appkit4-icon icon-warning-fill status-icon error-icon" />
                            );
                        case 'Awaiting Online Form Generation':
                            return (
                                <span className="status-dot pending-icon">
                                    ●
                                </span>
                            );
                        case 'Online Form Generation Started':
                            return (
                                <span className="status-dot pending-icon">
                                    ●
                                </span>
                            );
                        case 'Online Form Generation System Error':
                            return (
                                <span className="Appkit4-icon icon-warning-fill status-icon error-icon" />
                            );
                        case 'Online Form Generated Successfully':
                            return (
                                <span className="status-dot success-icon">
                                    ●
                                </span>
                            );
                        case 'Fill PDF Generated Successfully':
                            return (
                                <span className="status-dot success-icon">
                                    ●
                                </span>
                            );
                        case 'Awaiting Fill PDF Generation':
                            return (
                                <span className="status-dot pending-icon">
                                    ●
                                </span>
                            );
                        default:
                            return null;
                    }
                };

                return (
                    <div className="d-flex align-items-center gap-2">
                        {getStatusIcon(getStatusByNumber(row.processStatus))}
                        <span>{getStatusByNumber(row.processStatus)}</span>
                    </div>
                );
            },
        },
        {
            field: 'lastUpdated',
            header: 'Last Updated',
            renderCell: (row: any) => (
                <span>{formatDate(row.lastUpdated)}</span>
            ),
        },
        ...(processTypeId === 3
            ? [
                  {
                      field: 'readyForSubmission',
                      header: 'Ready For Submission',
                      renderCell: (row: any) => (
                          <span>
                              <Switch
                                  checked={!!row.readyForSubmission}
                                  onChange={(checked) =>
                                      handleReadyForSubmissionToggle(
                                          row.documentId,
                                          checked
                                      )
                                  }
                              />
                          </span>
                      ),
                  },
              ]
            : []),
        {
            field: 'documentType',
            header: 'Edit',
            style: { width: '50px' },
            renderCell: (row: any) => {
                const statusText = getStatusByNumber(row.processStatus);

                if (
                    statusText !== 'System Error' &&
                    statusText !== 'Awaiting Validation' &&
                    statusText !== 'Awaiting Extraction' &&
                    statusText !== 'Awaiting Online Form Generation' &&
                    statusText !== 'Online Form Generation Started' &&
                    statusText !== 'Online Form Generation System Error' &&
                    statusText !== 'Awaiting Fill PDF Generation'
                ) {
                    return (
                        <span
                            className="Appkit4-icon icon-edit-outline action-icon"
                            role="button"
                            onClick={() => handleEdit(row)}
                            aria-label="Edit"
                        />
                    );
                } else {
                    return (
                        <span
                            className="Appkit4-icon icon-edit-outline action-icon-disabled"
                            role="button"
                            aria-label="Edit"
                        />
                    );
                }
            },
        },
        {
            field: 'documentType',
            header: 'Delete',
            style: { width: '50px' },
            renderCell: (row: any) => (
                <span
                    className="Appkit4-icon icon-delete-outline action-icon"
                    role="button"
                    onClick={() => handleDelete(row)}
                    aria-label="Delete"
                />
            ),
        },
    ];

    const handleTabChange = (index: number) => {
        if (onTabChange) {
            onTabChange(index);
        }
        // Update URL state when tab changes
        navigate(`/process/${processId}/uploaded`, {
            state: { onlineFormsTab: index },
            replace: true,
        });
    };

    if (isLoading) {
        return (
            <div className="processing-table loading-state">
                <Loading loadingType="circular" indeterminate compact={false} />
            </div>
        );
    }

    const deleteModalContent = () => (
        <div className="del-modal-content-wrapper">
            <img
                src={Illustration6}
                alt="Delete Confirmation"
                className="del-modal-image"
            />
            <p>Are you sure you want to delete this document?</p>
            <Input
                type="text"
                placeholder="Delete"
                className="del-folder-confirmation-input"
                value={inputValue}
                onChange={(value: string) => setInputValue(value)}
            />
            <p style={{ fontSize: 14 }}>
                Type “Delete” To Delete This Document
            </p>
            <div style={{ marginTop: '20px' }}>
                <p
                    id="misclassification-radio-group-title"
                    style={{ marginBottom: '10px' }}
                >
                    Report as Misclassification
                </p>
                <RadioGroup
                    aria-labelledby="misclassification-radio-group-title"
                    name="misclassification"
                    value={reportMisclassification.toString()}
                    onChange={(value: string) =>
                        setReportMisclassification(value === 'true')
                    }
                >
                    <Radio value="true">Yes</Radio>
                    <Radio value="false">No</Radio>
                </RadioGroup>
            </div>
            <Button
                onClick={confirmDeletion}
                kind="danger"
                disabled={inputValue.toLowerCase() !== 'delete'}
                style={{ marginTop: '40px' }}
            >
                Delete
            </Button>
        </div>
    );

    return (
        <div className="processing-table">
            <ListingTable
                tabs={tabs}
                activeTab={activeTab}
                columns={columns}
                data={filterByDocType(
                    activeTab === 1
                        ? filterActionData(filteredData)
                        : activeTab === 2
                        ? filterCompleteData(filteredData)
                        : activeTab === 3
                        ? filterReadyForSubmissionData(filteredData)
                        : filteredData
                )}
                totalCount={
                    filterByDocType(
                        activeTab === 1
                            ? filterActionData(filteredData)
                            : activeTab === 2
                            ? filterCompleteData(filteredData)
                            : activeTab === 3
                            ? filterReadyForSubmissionData(filteredData)
                            : filteredData
                    ).length
                }
                currentPage={currentPage}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onTabChange={handleTabChange}
                selectedIndexs={selectedIds}
                onSelectedChange={setSelectedIds}
                checkableColumn={{
                    field: 'documentId',
                    header: 'Document ID',
                    renderCell: (row) => {
                        const isFormGenerated =
                            row.processStatus === 16 ||
                            row.processStatus === 18;
                        return (
                            <div className="d-flex align-items-center gap-2">
                                <span>{row.documentId}</span>
                                <span
                                    className={`Appkit4-icon icon-open-in-new-window-outline ${
                                        isFormGenerated
                                            ? 'window-icon'
                                            : 'action-icon-disabled'
                                    }`}
                                    role="button"
                                    aria-label="Window"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (isFormGenerated) {
                                            handleExpand(row);
                                        }
                                    }}
                                />
                            </div>
                        );
                    },
                }}
                headerButtons={
                    <div className="table-header-buttons">
                        <Button
                            kind="primary"
                            icon="icon-document-export-outline"
                            onClick={handleExport}
                            loading={isExporting}
                            disabled={
                                isExporting ||
                                !areAllSelectedDocumentsReadyForExport
                            }
                        >
                            {(processTypeId === 2 || processTypeId === 3) &&
                            selectedIds.length > 0
                                ? `Export documents (${selectedIds.length})`
                                : 'Export'}
                        </Button>
                        <Button
                            kind="secondary"
                            icon="icon-upload-outline"
                            onClick={handleUpload}
                        >
                            Upload documents
                        </Button>
                    </div>
                }
            />

            <ExportModal
                visible={isExportModalVisible}
                onClose={() => setIsExportModalVisible(false)}
                processId={processId}
                processTypeId={processTypeId}
            />

            <UploadModal
                visible={isUploadModalVisible}
                onClose={() => setIsUploadModalVisible(false)}
                onUpload={handleUploadComplete}
                processId={processId}
                processTypeId={processTypeId}
                documentTypeIds={processDocumentTypeIds}
            />

            <DeleteModal
                content={deleteModalContent()}
                visible={confirmationVisible}
                onClose={() => setConfirmationVisible(false)}
                title="Delete Document"
            />

            {selectedDocument && (
                <PDFViewerModal
                    visible={isPdfModalVisible}
                    onClose={() => {
                        setIsPdfModalVisible(false);
                        setSelectedDocument(null);
                    }}
                    documentId={selectedDocument}
                />
            )}
        </div>
    );
};

export default OnlineFormFillerTable;
